var _excluded = ["value", "onChange", "minDate", "maxDate", "excludeHolidays", "weekDays", "defaultIsOpen", "isLoading", "legendItems", "size", "secondarySelectedDate", "showToday", "isDisabled", "isReadOnly", "isViewMode", "hideClear", "format", "toggleDatePickerAriaLabel", "clearDateAriaLabel"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { autoUpdate, flip, FloatingFocusManager, offset, shift, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { formatDateTime } from '@melio/penny-utils';
import { useMemo, useState } from 'react';
import { Card, Group } from '../../../containers';
import { forwardRef, useBoolean, useUpdateEffect } from '../../../utils';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { IconButton } from '../../action';
import { _BaseCalendar, Box, Portal } from '../../internal';
import { TextField } from '../TextField';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var TOOLTIP_OFFSET = 8;
var TOOLTIP_SHIFT_PADDING = 24;
var PLACEMENT = 'bottom-end';
var TOOLTIP_POSITION = 'absolute';
export var DateField = forwardRef((_ref, ref) => {
  var {
    value,
    onChange,
    minDate,
    maxDate,
    excludeHolidays = true,
    weekDays = [1, 2, 3, 4, 5],
    defaultIsOpen,
    isLoading,
    legendItems,
    size = 'large',
    secondarySelectedDate,
    showToday = true,
    isDisabled,
    isReadOnly,
    isViewMode,
    hideClear,
    format,
    toggleDatePickerAriaLabel = 'Toggle date picker',
    clearDateAriaLabel = 'Clear'
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var isEnabled = !isReadOnly && !isViewMode && !isDisabled;
  var [isFocused, setIsFocused] = useBoolean(false);
  var [isHover, setIsHover] = useBoolean(false);
  var [isOpen, setIsOpen] = useState(defaultIsOpen && isEnabled);
  var [selectedDate, setSelectedDate] = useState(value || undefined);
  var formattedValue = useMemo(() => formatDateTime(selectedDate, {
    format
  }), // eslint-disable-next-line react-hooks/exhaustive-deps
  [selectedDate]);
  var style = useMultiStyleConfig('DateField', props);
  useUpdateEffect(() => {
    value !== selectedDate && (onChange === null || onChange === void 0 ? void 0 : onChange(selectedDate !== null && selectedDate !== void 0 ? selectedDate : null));
  }, [selectedDate]);
  useUpdateEffect(() => {
    setSelectedDate(value);
  }, [value]);
  var iconStateProps = {};

  if (isReadOnly) {
    iconStateProps['data-readonly'] = true;
  }

  if (isViewMode) {
    iconStateProps['data-view-mode'] = true;
  }

  if (isDisabled) {
    iconStateProps['data-disabled'] = true;
  }

  if (isFocused || isHover) {
    iconStateProps['data-active'] = true;
  }

  var selectDate = value => {
    setSelectedDate(value);
    setIsOpen(false);
  };

  var onOpenChange = open => {
    setIsOpen(isEnabled && open);
  };

  var {
    x,
    y,
    refs,
    context
  } = useFloating({
    placement: PLACEMENT,
    strategy: TOOLTIP_POSITION,
    open: isOpen,
    onOpenChange,
    middleware: [offset(TOOLTIP_OFFSET), flip(), shift({
      padding: TOOLTIP_SHIFT_PADDING
    })],
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, {
      animationFrame: true,
      elementResize: false
    })
  });
  var {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([useClick(context), useDismiss(context)]);
  var fieldHasDate = value || selectedDate;
  var hideClearIcon = fieldHasDate && isEnabled ? hideClear : true;

  var reset = e => {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    setIsOpen(false);
    e === null || e === void 0 ? void 0 : e.stopPropagation(); // avoid opening the calendar

    if (isEnabled) setSelectedDate(undefined);
  };

  var rightElement = /*#__PURE__*/_jsxs(Group, {
    spacing: "xs",
    alignItems: "center",
    children: [!hideClearIcon && /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({}, iconStateProps), {}, {
      __css: style['closeIcon'],
      children: /*#__PURE__*/_jsx(IconButton, {
        "aria-label": clearDateAriaLabel,
        "data-testid": "date-input-clear",
        variant: "naked",
        icon: "close",
        onClick: isEnabled ? reset : undefined,
        size: "small"
      })
    })), /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({}, iconStateProps), {}, {
      __css: style['calendarIcon'],
      children: /*#__PURE__*/_jsx(IconButton, {
        "aria-label": toggleDatePickerAriaLabel,
        icon: "calendar",
        variant: "naked",
        size: "small"
        /* used to set the correct color to the IconButton. */
        ,
        isDisabled: isDisabled || isReadOnly
      })
    }))]
  });

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread(_objectSpread({}, getReferenceProps()), {}, {
      type: "input",
      sx: style['trigger'],
      onFocus: isEnabled ? setIsFocused.on : undefined,
      onBlurCapture: isEnabled ? setIsFocused.off : undefined,
      onChange: () => null,
      onMouseOver: setIsHover.on,
      onMouseOut: setIsHover.off,
      "data-focus": isEnabled && (isOpen || isFocused) || null,
      "data-component": "DateField",
      value: formattedValue,
      size: size,
      isLoading: isLoading,
      isDisabled: isDisabled,
      isReadOnly: isReadOnly,
      isViewMode: isViewMode
    }, props), {}, {
      ref: ref,
      containerRef: refs.setReference,
      rightElement: rightElement
    })), /*#__PURE__*/_jsx(Portal, {
      children: isOpen && /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(FloatingFocusManager, {
          context: context,
          children: /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({
            sx: _objectSpread({}, style['popover']),
            style: {
              top: y !== null && y !== void 0 ? y : 0,
              left: x !== null && x !== void 0 ? x : 0,
              position: TOOLTIP_POSITION
            }
          }, getFloatingProps()), {}, {
            role: "dialog",
            ref: refs.setFloating,
            children: /*#__PURE__*/_jsx(Card, {
              paddingX: "none",
              paddingY: "none",
              children: /*#__PURE__*/_jsx(Box, {
                __css: style['dateInputCalendar'],
                children: /*#__PURE__*/_jsx(_BaseCalendar, {
                  "data-testid": "date-input-calendar",
                  size: "small",
                  selectedDate: selectedDate,
                  onSelect: selectDate,
                  minDate: minDate,
                  maxDate: maxDate,
                  weekDays: weekDays,
                  excludeHolidays: excludeHolidays,
                  legendItems: legendItems,
                  secondarySelectedDate: secondarySelectedDate,
                  showToday: showToday
                })
              })
            })
          }))
        })
      })
    })]
  });
});
DateField.displayName = 'DateField';
//# sourceMappingURL=DateField.js.map